var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import './request-idle-callback-polyfill.js';
import './inert.js';
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { debounce } from './debounce.js';
import { FocusScopeChangeEvent } from './focus-scope-event.js';
import { focusTrapPosition } from './focus-trap-position.js';
import { isFocusable, isHidden } from './focusable.js';
import { queryShadowRoot } from './query-shadow-root.js';
/**
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-focus-scope-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `fs-focus-scope` call
 *
 * ```javascript
 * import '@cof/fs-focus-scope-wc/fs-focus-scope.js';
 * ```
 *
 * To import the `FsFocusScope` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FocusScope } from '@cof/fs-focus-scope-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 * <fs-focus-scope state="trap" @focus-scope-change=${() => {}}>
 *   ...
 * </fs-focus-scope>
 * ```
 *
 * ### The `focus-initial` attribute
 * _This is only applicable when the `state` attribute is set to `"trap"`._
 *
 * By default, the `FsFocusScope` will focus the first focusable element in the context of the FsFocusScope when set to
 * trap focus. This behavior can be overridden by putting a `focus-initial` attribute on an element in the context of
 * the FsFocusScope. If the element upon which this attribute is placed is focusable, it will be the initial thing
 * focused when the focus trap is enabled.
 *
 * @element fs-focus-scope
 * @fires focus-scope-change fired when the focus scope state changes
 */
export class FsFocusScope extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The component's current scope state (inactive, inert or trap).
         */
        this.state = null;
        /**
         * Whether or not the element is currently focused within.
         */
        this._focused = false;
        /**
         * Called when the element gets focused
         */
        this._onFocusIn = () => {
            this._updateFocused(true);
        };
        /**
         * Called whenever some blur event happens
         */
        this._onFocusOut = () => {
            this._updateFocused(false);
        };
    }
    static get styles() {
        return [
            /* Positions the focus-bound catchers at the middle of the screen to not cause any scrolling when they are hit. */
            css `
        .fs-focus-scope__focus-bound-catcher {
          position: fixed;
          top: 50vh;
        }
      `
        ];
    }
    /**
     * Whether or not the element is currently focused within.
     */
    get focused() {
        return this._focused;
    }
    /**
     * The initial element to focus on (using the ['focus-initial'] attribute).
     */
    get focusInitial() {
        const focusInitialElements = queryShadowRoot(this, isHidden, 
        /*
         * TODO:
         * Add a custom-element-friendly method of validating that the element is focusable instead of blindly trusting
         * the developer.
         *
         * CONTEXT:
         * This originally had functionality in place to determine whether or not the element was focusable. As a
         * result, if a developer tried to focus a non-focusable element initially, it would fail and the first default
         * focusable element would be focused. This would be desirable, but the only method we currently have in place for
         * determining whether or not an element is naive and breaks as soon as it sees a web component. We have a current
         * need to support focusable custom-elements in this and so this functionality was removed.
         */
        (element) => element.hasAttribute('focus-initial'));
        if (focusInitialElements == null || focusInitialElements.length === 0) {
            return null;
        }
        return focusInitialElements[0];
    }
    /**
     * Whether or not the user cannot be trapped
     */
    get _untrappable() {
        return !this.focused || this.state !== 'trap';
    }
    /**
     * To be used in conjunction with trapFocus.
     * Clears the trap state, will refocus the user to wherever focus was
     * when trapFocus was called
     */
    clearTrap() {
        if (this.state !== 'trap') {
            return;
        }
        this.state = this._previousFocusScopeState;
        if (this._previousActiveElement != null) {
            this._previousActiveElement.focus();
        }
        this._previousFocusScopeState = null;
        this._previousActiveElement = null;
    }
    /**
     * Add event listeners.
     */
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('focusin', this._onFocusIn);
        this.addEventListener('focusout', this._onFocusOut);
    }
    /**
     * Remove event listeners.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('focusin', this._onFocusIn);
        this.removeEventListener('focusout', this._onFocusOut);
    }
    /**
     * Check if inert attribute needs to be applied.
     */
    firstUpdated() {
        window.requestIdleCallback(() => {
            this.toggleAttribute('inert', this.state === 'inert');
        });
        this._toggleInternalTabIndeces();
    }
    /**
     * Manage focus for this element naively
     */
    focus() {
        if (this.state === 'inert') {
            return;
        }
        if (this.state === 'trap') {
            const target = this.focusInitial || this._getFocusableElements()[0];
            target?.focus?.();
            return;
        }
        this._getFocusableElements()[0]?.focus?.();
    }
    /**
     * Move focus to the first element.
     */
    focusFirstElement() {
        this._trapFocus('start');
    }
    /**
     * Move focus to the last element.
     */
    focusLastElement() {
        this._trapFocus('end');
    }
    /**
     * Render the component.
     *
     * @returns the rendered component
     */
    render() {
        return html `
      <div class="fs-focus-scope__focus-bound-catcher" id="start" @focus="${this.focusLastElement}"></div>
      <div class="fs-focus-scope__focus-bound-catcher" id="back"></div>
      <slot></slot>
      <div class="fs-focus-scope__focus-bound-catcher" id="end" @focus="${this.focusFirstElement}"></div>
    `;
    }
    /**
     * Switches the element's state to trap and saves a refernce
     * to the previously-focused element so the focus can be reset
     * later with a call to clearTrap.
     */
    trapFocus() {
        this._previousActiveElement = document.activeElement;
        this._previousFocusScopeState = this.state;
        this.removeAttribute('inert');
        window.requestIdleCallback(() => {
            this.state = 'trap';
            this.focus();
        });
    }
    /**
     * Handles changes to the
     *
     * @param changedProperties
     * @fires FocusScope#focus-scope-change
     */
    updated(changedProperties) {
        if (changedProperties.has('state')) {
            const previousState = changedProperties.get('state');
            if (this.state !== previousState) {
                this.dispatchEvent(new FocusScopeChangeEvent(previousState, this.state));
            }
            window.requestIdleCallback(() => {
                this.toggleAttribute('inert', this.state === 'inert');
            });
        }
    }
    /**
     * Gets the focusable elements slotted in this component.
     *
     * @returns the focusable elements slotted int his component
     */
    _getFocusableElements() {
        return queryShadowRoot(this, isHidden, isFocusable);
    }
    /**
     * Toggle tab index for shadow DOM elements
     */
    _toggleInternalTabIndeces() {
        this._startElement.setAttribute('tabindex', this._untrappable ? '-1' : '0');
        this._endElement.setAttribute('tabindex', this._untrappable ? `-1` : `0`);
        this.toggleAttribute('focused', this.focused);
    }
    /**
     * Trap focus in the FocusScope.
     *
     * @param trapPosition where to start trapping
     */
    _trapFocus(trapPosition = focusTrapPosition.start) {
        if (this.state !== 'trap') {
            return;
        }
        const focusableElements = this._getFocusableElements();
        if (focusableElements.length > 0) {
            if (trapPosition === 'start') {
                focusableElements[0].focus();
            }
            else {
                focusableElements[focusableElements.length - 1].focus();
            }
            this._backElement.setAttribute('tabindex', '-1');
        }
        else {
            this._backElement.setAttribute('tabindex', '0');
            this._backElement.focus();
        }
    }
    /**
     * Debounce the call to _render for performance reasons.
     *
     * @param value whether or not an element is focused
     */
    _updateFocused(value) {
        debounce(() => {
            if (this.focused !== value) {
                this._focused = value;
                this._toggleInternalTabIndeces();
            }
        }, 0)();
    }
}
FsFocusScope.canonicalName = 'focus-scope';
__decorate([
    property({ type: String, reflect: true })
], FsFocusScope.prototype, "state", void 0);
__decorate([
    query('#back')
], FsFocusScope.prototype, "_backElement", void 0);
__decorate([
    query('#end')
], FsFocusScope.prototype, "_endElement", void 0);
__decorate([
    query('#start')
], FsFocusScope.prototype, "_startElement", void 0);
//# sourceMappingURL=FocusScope.js.map